import {
  TextAreaField,
  TextInputField,
  SimpleModal,
  Box,
  Flex,
  Link,
  GreyGrey60,
  IndigoIndigo60,
  Primary12Regular
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useMutation } from '@sequencehq/api'
import {
  dashboard20240730Client,
  type DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'

type Props = {
  trigger?: React.ReactElement
  isOpen?: boolean
  onClose?: () => void
  onCreate?: (
    seatMetric: DashboardApi20240730.PostSeatMetric.PostSeatMetricResponse
  ) => void
}

export const CreateSeatTypeDrawer = (props: Props) => {
  const { displayNotification } = useNotifications()
  const { mutateAsync: createSeatMetric } = useMutation(
    dashboard20240730Client.postSeatMetric,
    {
      onSuccess: seatMetric => {
        displayNotification('Seat metric created', {
          type: 'success'
        })

        if (seatMetric !== null && props.onCreate) {
          props.onCreate(seatMetric)
        }
      },
      onError: () => {
        displayNotification('Failed to create seat metric', {
          type: 'error'
        })
      }
    }
  )

  const form = useForm({
    value: {
      label: '',
      description: '',
      seatType: ''
    },
    fieldConfiguration: [
      { property: 'label', validation: [required] },
      { property: 'description' },
      { property: 'seatType', validation: [required] }
    ]
  })

  const handleSubmit = async () => {
    const seatMetric = await createSeatMetric({
      body: {
        label: form.queries.formData.label,
        description: form.queries.formData.description,
        seatType: form.queries.formData.seatType
      }
    })

    return !!seatMetric
  }

  const handleClose = () => {
    form.mutators.resetFormData()
    if (props.onClose) {
      props.onClose()
    }
  }

  const modalProps =
    'trigger' in props && props.trigger
      ? { trigger: props.trigger, onClose: handleClose }
      : {
          isOpen: Boolean(props.isOpen),
          onClose: handleClose
        }

  return (
    <SimpleModal
      {...modalProps}
      data-testid="seatMetric.drawer"
      variant="drawer"
      title="Add seat metric"
      cancelButtonText="Cancel"
      submitButtonText="Add seat metric"
      submittingButtonText="Adding seat metric..."
      submitDisabled={!form.queries.isValid}
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Flex flexDirection="column" gap="16px">
        <Box {...Primary12Regular} color={GreyGrey60}>
          Seat metrics reflect the types of seats customers are billed for (e.g.
          admin seats or viewer seats).
        </Box>

        <TextInputField
          data-testid="seatMetric.name"
          label="Name"
          placeholder="Responder"
          value={form.fields.label.value}
          onChange={e => form.fields.label.onChange(e.target.value)}
          validationErrors={form.fields.label.validationErrors}
        />
        <TextAreaField
          data-testid="seatMetric.description"
          label="Description"
          placeholder="Track usage for support seats"
          value={form.fields.description.value}
          onChange={form.fields.description.onChange}
          validationErrors={form.fields.description.validationErrors}
        />

        <Flex flexDirection="column" gap={2}>
          <TextInputField
            data-testid="seatMetric.seatType"
            label="Seat type"
            placeholder="support_seat"
            value={form.fields.seatType.value}
            onChange={e => form.fields.seatType.onChange(e.target.value)}
            validationErrors={form.fields.seatType.validationErrors}
          />
          <Box {...Primary12Regular} color={GreyGrey60}>
            Use the seat type to submit seat events and update the balance for a
            customer.{' '}
            <Link
              color={IndigoIndigo60}
              to="https://docs.sequencehq.com/getting-started/overview"
              target="_blank"
              rel="noreferrer noopener"
            >
              Learn more
            </Link>
            .
          </Box>
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
